import { Component, HostBinding, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined, isNil, ReadonlyLookup } from '@trimble-gcs/common';
import {
  ModusButtonModule,
  ModusFormFieldModule,
  ModusIconModule,
  ModusInputModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { distinctUntilChanged, filter, finalize, map, merge, switchMap, take } from 'rxjs';
import {
  MarkupChangedArgument,
  PointMarkup,
  PointPickedEventArgument,
  Vector3,
} from 'trimble-connect-workspace-api';
import { AlignmentCalculationsApiService, AlignmentMeasurementApiService } from '../../api';
import { convertFromMarkupUnit } from '../../connect';
import { UomPipe } from '../../pipes';
import { BusyIndicatorComponent, OVERLAY_DATA, OverlayReference } from '../../shared';
import { AppState, mapMeasurement, MeasurementViewModel } from '../../state';

enum MeasureState {
  None = 'None',
  Measuring = 'Select a position in the 3D viewer',
  Projecting = 'Projecting to alignment',
  Error = 'Error',
}

enum CantMode {
  Perpendicular = 'Perpendicular',
  Vertical = 'Vertical',
}

enum GradientMode {
  Perpendicular = 'Perpendicular',
  Vertical = 'Vertical',
}

interface ToggleOption<T> {
  mode: T;
  value: boolean;
}

type CantOption = ToggleOption<CantMode>;

type GradientOption = ToggleOption<GradientMode>;

const CantOptions: ReadonlyLookup<CantMode, CantOption> = {
  Perpendicular: {
    mode: CantMode.Perpendicular,
    value: false,
  },
  Vertical: {
    mode: CantMode.Vertical,
    value: true,
  },
};

const GradientOptions: ReadonlyLookup<GradientMode, GradientOption> = {
  Perpendicular: {
    mode: GradientMode.Perpendicular,
    value: false,
  },
  Vertical: {
    mode: GradientMode.Vertical,
    value: true,
  },
};

@UntilDestroy()
@Component({
  selector: 'nzc-measurement-detail',
  imports: [
    MatButtonToggleModule,
    ModusButtonModule,
    ModusFormFieldModule,
    ModusIconModule,
    ModusInputModule,
    ModusTooltipModule,
    ReactiveFormsModule,
    BusyIndicatorComponent,
    UomPipe,
  ],
  templateUrl: './measurement-detail.component.html',
  styleUrls: ['./measurement-detail.component.scss'],
})
export class MeasurementDetailComponent implements OnInit, OnDestroy {
  @HostBinding('class') componentClasses = 'flex flex-col grow';

  private readonly workspace = this.store.selectSnapshot(AppState.workspace);

  public MeasureState = MeasureState;
  public measureState = MeasureState.None;
  public saving = false;
  public projectionError?: string;
  public readonly cantOptions = Object.values(CantOptions);
  public readonly gradientOptions = Object.values(GradientOptions);

  public readonly formGroup = new FormGroup({
    name: new FormControl<string | null>('', Validators.required),
  });

  public cantControl = new FormControl<CantOption>(
    this.cantOptions.find((o) => o.value === this.measurement.useCant) as CantOption,
    {
      nonNullable: true,
    },
  );

  public gradientControl = new FormControl<GradientOption>(
    this.gradientOptions.find((o) => o.value === this.measurement.useGradient) as GradientOption,
    {
      nonNullable: true,
    },
  );

  constructor(
    private store: Store,
    private calculationsService: AlignmentCalculationsApiService,
    private measurementService: AlignmentMeasurementApiService,
    private overlayRef: OverlayReference<MeasurementViewModel>,
    @Inject(OVERLAY_DATA) public measurement: MeasurementViewModel,
  ) {
    merge(this.cantControl.valueChanges, this.gradientControl.valueChanges)
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(() => {
        this.recalculate();
      });
  }

  public get isNew(): boolean {
    return isNil(this.measurement.id);
  }

  public get isBusy(): boolean {
    return (
      this.measureState === MeasureState.Measuring || this.measureState === MeasureState.Projecting
    );
  }

  public async ngOnInit(): Promise<void> {
    if (this.isNew) {
      await this.startMeasurement();
    } else {
      this.formGroup.reset(this.measurement);
    }
  }

  public async ngOnDestroy(): Promise<void> {
    await this.stopMeasurement();
  }

  private async startMeasurement(): Promise<void> {
    this.measureState = MeasureState.Measuring;
    this.projectionError = undefined;

    // Activate single-point measurement
    if (!(await this.workspace.activateTool('pointMarkup'))) {
      this.measureState = MeasureState.None;
      return;
    }

    // Get current single point markups to be able to remove added markup
    const pointMarkups = await this.workspace.getSinglePointMarkups();

    // Listen for first markup/picked event, then project to alignment
    const pointMarkupAdded$ = this.workspace.event$.pipe(
      untilDestroyed(this),
      filter((event) => event.id === 'viewer.onMarkupChanged'),
      map((event) => event.arg as MarkupChangedArgument),
      filter((markupChange) =>
        isDefined(
          markupChange.data.markupType === 'pointMarkup' && markupChange.data.action === 'added',
        ),
      ),
      take(1),
      map((markupChange) => {
        const pointMarkup = markupChange.data.markup as PointMarkup;
        const position: Vector3 = {
          x: convertFromMarkupUnit(pointMarkup.start.positionX),
          y: convertFromMarkupUnit(pointMarkup.start.positionY),
          z: convertFromMarkupUnit(pointMarkup.start.positionZ),
        };
        return position;
      }),
    );

    const pointPicked$ = this.workspace.event$.pipe(
      untilDestroyed(this),
      filter((event) => event.id === 'viewer.onPicked'),
      map((event) => event.arg as PointPickedEventArgument),
      filter((pointSelection) => isDefined(pointSelection.data.position)),
      take(1),
      map((pointSelection) => pointSelection.data.position),
    );

    merge(pointMarkupAdded$, pointPicked$)
      .pipe(
        untilDestroyed(this),
        take(1),
        switchMap((position) => {
          this.measureState = MeasureState.Projecting;
          const useCant = this.cantControl.value.value;
          const useGradient = this.gradientControl.value.value;
          return this.calculationsService
            .projectToStation(this.measurement.alignmentId, position, useCant, useGradient)
            .pipe(map((projection) => ({ useCant, useGradient, projection })));
        }),
        finalize(async () => {
          await this.workspace.removeAddedSinglePointMarkups(pointMarkups);
          this.stopMeasurement();
        }),
      )
      .subscribe({
        next: async (result) => {
          this.measurement.projection = result.projection;
          this.measurement.useCant = result.useCant;
          this.measurement.useGradient = result.useGradient;
          await this.workspace.createMeasurementMarkup(this.measurement);
        },
        error: (err) => {
          this.projectionError = err;
        },
      });
  }

  public async recalculate(): Promise<void> {
    // Ensure required values are set
    const prevProjection = this.measurement.projection;
    if (isNil(prevProjection) || isNil(prevProjection.pointCoordinate)) {
      return;
    }

    // Remove previouse measurement markup
    await this.workspace.removeMeasurementMarkup(this.measurement);

    // Recalculate projection
    this.measureState = MeasureState.Projecting;
    this.measurement.projection = undefined;
    const useCant = this.cantControl.value.value;
    const useGradient = this.gradientControl.value.value;
    this.calculationsService
      .projectToStation(
        this.measurement.alignmentId,
        prevProjection.pointCoordinate,
        useCant,
        useGradient,
      )
      .pipe(finalize(async () => (this.measureState = MeasureState.None)))
      .subscribe({
        next: async (projection) => {
          // Update projection and create new measurement markup
          this.measurement.projection = projection;
          this.measurement.useCant = useCant;
          this.measurement.useGradient = useGradient;
          await this.workspace.createMeasurementMarkup(this.measurement);
        },
        error: (err) => {
          this.projectionError = err;
        },
      });
  }

  private async stopMeasurement(): Promise<void> {
    this.measureState = MeasureState.None;

    // Reset tools
    await this.workspace.activateTool('reset');
  }

  public async cancelChanges(): Promise<void> {
    // Remove unsaved markup
    if (this.isNew) {
      await this.workspace.removeMeasurementMarkup(this.measurement);
    }

    this.overlayRef.close();
  }

  public saveChanges(): void {
    this.measurement.name = this.formGroup.controls.name.value ?? '';

    const save$ = this.isNew
      ? this.measurementService.createMeasurement(this.measurement.alignmentId, this.measurement)
      : this.measurementService.updateMeasurement(this.measurement.alignmentId, this.measurement);

    this.saving = true;
    save$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: async (savedMeasurement) => {
        await this.workspace.removeMeasurementMarkup(this.measurement);

        this.overlayRef.close(mapMeasurement(savedMeasurement));
      },
      error: (err) => {
        // TODO: Show errors
        // this.toastr.error(err);
        console.error('saveChanges', err);
      },
    });
  }
}
